/**
 * Combined Listings: fetch variant data by product URL only for option inputs that have
 * data-fetch-variant="true" (set by Liquid when variant was blank). Runs only inside product cards.
 */
(function () {
  const cache = new Map();

  function buildProductJsonUrl(productUrl) {
    const base = (productUrl || '').split('?')[0];
    if (!base) return '';
    const path = base.startsWith('http') ? base : base.startsWith('/') ? base : `/${base}`;
    return `${path}.js`;
  }

  function fullUrl(path) {
    if (path.startsWith('http')) return path;
    const origin = window.location?.origin || '';
    return origin + (path.startsWith('/') ? path : `/${path}`);
  }

  function getOptionValueName(input) {
    if (!input?.id) return '';
    const label = document.querySelector(`label[for="${input.id}"]`);
    return (label?.textContent || '').trim();
  }

  function findVariant(product, optionValueName, optionPosition) {
    if (!product?.variants?.length) return null;
    const pos = parseInt(optionPosition, 10) || 1;
    const name = (optionValueName || '').trim();
    const v = product.variants.find(
      (variant) =>
        (pos === 1 && variant.option1 === name) ||
        (pos === 2 && variant.option2 === name) ||
        (pos === 3 && variant.option3 === name)
    );
    return v || product.variants[0];
  }

  /**
   * Normalize media from product JSON to match Liquid output (alt, id, position, preview_image, aspect_ratio, height, media_type, src, width).
   */
  function normalizeMedia(media) {
    if (!media) return null;
    const preview = media.preview_image || media;
    const img = preview.src ? preview : { aspect_ratio: 1, height: 0, width: 0, src: '' };
    return {
      alt: media.alt ?? null,
      id: media.id,
      position: media.position ?? 1,
      preview_image: {
        aspect_ratio: img.aspect_ratio ?? 1,
        height: img.height ?? 0,
        width: img.width ?? 0,
        src: img.src || media.src || '',
      },
      aspect_ratio: media.aspect_ratio ?? img.aspect_ratio ?? 1,
      height: media.height ?? img.height ?? 0,
      media_type: media.media_type || 'image',
      src: media.src || img.src || '',
      width: media.width ?? img.width ?? 0,
    };
  }

  function setVariantDataOnInput(input, variant, product) {
    if (!variant) return;
    input.setAttribute('data-variant-id', String(variant.id));
    const mediaList = product?.media || product?.images || [];
    const featuredMedia = variant.featured_media || variant.featured_image || (mediaList.length ? mediaList[0] : null);
    if (featuredMedia) {
      const mediaObj = normalizeMedia(featuredMedia);
      input.setAttribute('data-variant-media', JSON.stringify(mediaObj));
    }
    const secondaryMedia = mediaList[1] || (featuredMedia && mediaList.length ? mediaList[0] : null);
    if (secondaryMedia) {
      const secondaryObj = normalizeMedia(secondaryMedia);
      input.setAttribute('data-variant-secondary-media', JSON.stringify(secondaryObj));
    }
  }

  /** Only run for inputs that have data-fetch-variant="true" (Liquid set when variant was blank). */
  function shouldFetchForInput(input) {
    return input && input.getAttribute('data-fetch-variant') === 'true';
  }

  /**
   * Fetch product JSON and populate variant data on the input.
   * Only runs when input has data-fetch-variant="true".
   * @param {HTMLInputElement} input - Option input with data-fetch-variant="true" and data-product-url.
   * @returns {Promise<boolean>} - true if variant data was set, false otherwise.
   */
  function ensureVariantData(input) {
    if (!shouldFetchForInput(input)) return Promise.resolve(false);
    if (!input.dataset.productUrl) return Promise.resolve(false);
    if (input.dataset.variantId) return Promise.resolve(true);

    const productUrl = input.dataset.productUrl;
    const jsonUrl = buildProductJsonUrl(productUrl);
    if (!jsonUrl) return Promise.resolve(false);

    const cacheKey = fullUrl(jsonUrl);
    let promise = cache.get(cacheKey);
    if (!promise) {
      promise = fetch(fullUrl(jsonUrl))
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null);
      cache.set(cacheKey, promise);
    }

    return promise.then((product) => {
      if (!product) return false;
      const optionValueName = getOptionValueName(input);
      const optionPosition = input.dataset.optionPosition || '1';
      const variant = findVariant(product, optionValueName, optionPosition);
      if (variant) {
        setVariantDataOnInput(input, variant, product);
        return true;
      }
      return false;
    });
  }

  /**
   * Run ensureVariantData only for inputs that have data-fetch-variant="true" (and no data-variant-id yet).
   */
  function backfillVariantData(container) {
    const root = container || document;
    const inputs = root.querySelectorAll(
      'input[data-fetch-variant="true"]:not([data-variant-id])'
    );
    return Promise.all(Array.from(inputs).map((input) => ensureVariantData(input)));
  }

  function onIntersection(entries, observer) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      observer.unobserve(el);
      backfillVariantData(el);
    });
  }

  function init() {
    const containers = document.querySelectorAll('product-card');
    const observer =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(onIntersection, { rootMargin: '200px', threshold: 0 })
        : null;

    containers.forEach((container) => {
      if (observer) {
        observer.observe(container);
      } else {
        backfillVariantData(container);
      }
    });

    // On hover: only fetch when the input has data-fetch-variant="true"
    document.addEventListener(
      'pointerover',
      (e) => {
        const label = e.target.closest('label[for]');
        if (!label?.htmlFor) return;
        const input = document.getElementById(label.htmlFor);
        if (!input) return;
        if (!shouldFetchForInput(input)) return;
        if (input.dataset.variantId) return;
        ensureVariantData(input);
      },
      { passive: true, capture: true }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.CombinedListingsVariantFetch = {
    ensureVariantData,
    backfillVariantData,
  };
})();
